import type { StateCreator } from 'zustand'
import type {
  AppScope,
  Collaborator,
  EventSubscription,
  SecuritySettings,
} from '@/types'
import type { AppState } from './types'

/** 权限 / 事件 / 安全设置 / 协作者：按 app_id 存的应用配置 */
export interface AppConfigSlice {
  scopes: Record<string, AppScope[]>
  events: Record<string, EventSubscription[]>
  security: Record<string, SecuritySettings>
  collaborators: Record<string, Collaborator[]>

  /** 首次进入时补空的权限 / 协作者配置，不写入假用户或假已开通项 */
  ensureAppConfig: (app_id: string) => void
  openScopes: (app_id: string, rowIds: string[]) => void
  closeScopes: (app_id: string, rowIds: string[]) => void
  toggleEvent: (app_id: string, eventId: string) => void
  addEvents: (app_id: string, items: EventSubscription[]) => void
  removeEvent: (app_id: string, eventId: string) => void
  addSecurityEntry: (
    app_id: string,
    field: keyof SecuritySettings,
    value: string,
  ) => void
  updateSecurityEntry: (
    app_id: string,
    field: keyof SecuritySettings,
    prev: string,
    next: string,
  ) => void
  removeSecurityEntry: (
    app_id: string,
    field: keyof SecuritySettings,
    value: string,
  ) => void
  replaceSecurityList: (
    app_id: string,
    field: keyof SecuritySettings,
    items: string[],
  ) => void
}

const emptySecurity = (): SecuritySettings => ({
  redirect_allowlist: [],
  ip_allowlist: [],
  h5_trusted_domains: [],
})

/** 给新出现的 app_id 补空配置；已有的保持原引用不变 */
export function seedAppConfig(
  state: Pick<AppConfigSlice, 'scopes' | 'events' | 'security' | 'collaborators'>,
  app_id: string,
): Pick<AppConfigSlice, 'scopes' | 'events' | 'security' | 'collaborators'> {
  return {
    scopes: state.scopes[app_id]
      ? state.scopes
      : { ...state.scopes, [app_id]: [] },
    events: state.events[app_id]
      ? state.events
      : { ...state.events, [app_id]: [] },
    security: state.security[app_id]
      ? state.security
      : { ...state.security, [app_id]: emptySecurity() },
    collaborators: state.collaborators[app_id]
      ? state.collaborators
      : { ...state.collaborators, [app_id]: [] },
  }
}

export const createConfigSlice: StateCreator<AppState, [], [], AppConfigSlice> = (
  set,
) => ({
  scopes: {},
  events: {},
  security: {},
  collaborators: {},

  ensureAppConfig: (app_id) =>
    set((state) => {
      if (state.scopes[app_id]) return state
      return seedAppConfig(state, app_id)
    }),

  /** 权限页已改走接口，不再把 mock 目录写入本地 */
  openScopes: () => {},

  closeScopes: (app_id, rowIds) =>
    set((state) => {
      const idSet = new Set(rowIds)
      return {
        scopes: {
          ...state.scopes,
          [app_id]: (state.scopes[app_id] ?? []).filter((s) => !idSet.has(s.id)),
        },
      }
    }),

  toggleEvent: (app_id, eventId) =>
    set((state) => ({
      events: {
        ...state.events,
        [app_id]: (state.events[app_id] ?? []).map((e) =>
          e.id === eventId ? { ...e, enabled: !e.enabled } : e,
        ),
      },
    })),

  addEvents: (app_id, items) =>
    set((state) => {
      const current = state.events[app_id] ?? []
      const exist = new Set(current.map((e) => e.event))
      const extra = items.filter((item) => !exist.has(item.event))
      if (extra.length === 0) return {}
      return {
        events: { ...state.events, [app_id]: [...current, ...extra] },
      }
    }),

  removeEvent: (app_id, eventId) =>
    set((state) => ({
      events: {
        ...state.events,
        [app_id]: (state.events[app_id] ?? []).filter((e) => e.id !== eventId),
      },
    })),

  addSecurityEntry: (app_id, field, value) =>
    set((state) => {
      const current = state.security[app_id] ?? emptySecurity()
      const trimmed = value.trim()
      if (!trimmed || current[field].includes(trimmed)) return {}
      return {
        security: {
          ...state.security,
          [app_id]: { ...current, [field]: [...current[field], trimmed] },
        },
      }
    }),

  updateSecurityEntry: (app_id, field, prev, next) =>
    set((state) => {
      const current = state.security[app_id] ?? emptySecurity()
      const trimmed = next.trim()
      if (!trimmed) return {}
      const list = current[field]
      const idx = list.indexOf(prev)
      if (idx < 0) return {}
      if (list.some((item, i) => i !== idx && item === trimmed)) return {}
      const copy = [...list]
      copy[idx] = trimmed
      return {
        security: {
          ...state.security,
          [app_id]: { ...current, [field]: copy },
        },
      }
    }),

  removeSecurityEntry: (app_id, field, value) =>
    set((state) => {
      const current = state.security[app_id]
      if (!current) return {}
      return {
        security: {
          ...state.security,
          [app_id]: {
            ...current,
            [field]: current[field].filter((v) => v !== value),
          },
        },
      }
    }),

  replaceSecurityList: (app_id, field, items) =>
    set((state) => {
      const current = state.security[app_id] ?? emptySecurity()
      return {
        security: {
          ...state.security,
          [app_id]: { ...current, [field]: items },
        },
      }
    }),
})
